import MainHeader from '@/components/MainHeader';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Check, X, Palette, Target, Clock, Award, TrendingUp, Heart } from 'lucide-react';
import profilaturaImg from '@/assets/profilatura.png';
import SEOHead from '@/components/SEOHead';
import { ServiceSchema, BreadcrumbSchema } from '@/components/SchemaOrg';

const Profilatura = () => {
  const videoId = '1vK5Yq8IUucfcn-3sma_J2YEaRkGxCl_Y';
  const videoEmbedUrl = `https://drive.google.com/file/d/${videoId}/preview`;

  const benefits = [
    {
      icon: <Palette className="w-8 h-8" />,
      title: 'Colori Accurati',
      description: 'Ottieni la corrispondenza perfetta tra colori digitali e stampati, garantendo che ogni lavoro rispetti le aspettative del cliente.'
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: 'Qualità Costante',
      description: 'Elimina le variazioni tra stampe diverse dello stesso file, assicurando risultati uniformi e prevedibili ogni volta.'
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: 'Risparmio Materiali',
      description: 'Riduci drasticamente gli sprechi eliminando ristampe dovute a colori errati, risparmiando tempo e denaro.'
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: 'Professionalità',
      description: 'Distinguiti dalla concorrenza con risultati di qualità professionale che soddisfano gli standard più elevati.'
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: 'Efficienza Produttiva',
      description: 'Accelera il flusso di lavoro eliminando tentativi ed errori, permettendoti di completare più ordini in meno tempo.'
    },
    {
      icon: <Heart className="w-8 h-8" />,
      title: 'Soddisfazione Cliente',
      description: 'Consegna sempre risultati perfetti al primo tentativo, aumentando la fedeltà e le recensioni positive.'
    }
  ];

  const steps = [
    {
      number: '01',
      title: 'Analisi del Tuo Setup',
      description: 'Studiamo la tua stampante, inchiostri, pellicola e polvere adesiva per comprendere le caratteristiche uniche del tuo sistema.'
    },
    {
      number: '02',
      title: 'Stampa dei Target',
      description: 'Utilizziamo chart di calibrazione specifici per misurare come il tuo sistema riproduce i colori standard.'
    },
    {
      number: '03',
      title: 'Misurazione Spettrofotometrica',
      description: 'Misuriamo con strumenti professionali ogni campione di colore per creare una mappa cromatica precisa.'
    },
    {
      number: '04',
      title: 'Creazione Profilo ICC',
      description: 'Generiamo un profilo colore personalizzato che compensa le deviazioni del tuo sistema rispetto allo standard.'
    },
    {
      number: '05',
      title: 'Installazione e Test',
      description: 'Ti forniamo il profilo e ti guidiamo nell\'installazione, verificando insieme i risultati con stampe di prova.'
    }
  ];

  const withoutProfiling = [
    'Colori imprevedibili e inconsistenti',
    'Sprechi di materiali per ristampe',
    'Tempo perso in aggiustamenti manuali',
    'Clienti insoddisfatti e reclami',
    'Risultati amatoriali'
  ];

  const withProfiling = [
    'Colori accurati al primo colpo',
    'Zero sprechi, massima efficienza',
    'Flusso di lavoro rapido e prevedibile',
    'Clienti entusiasti e fedeli',
    'Standard professionale certificato'
  ];

  return (
    <>
      <SEOHead
        title="Profilatura ICC Stampanti DTF | Calibrazione Colori Professionale | Materialidtf.it"
        description="Servizio di profilatura ICC professionale per stampanti DTF. Ottieni colori accurati, riduci sprechi e aumenta la qualità delle tue stampe. Consulenza gratuita."
        canonical="/profilatura"
        keywords="profilatura ICC, calibrazione colori DTF, profilo ICC stampante DTF, colori accurati stampa DTF, profilatura professionale"
      />
      <ServiceSchema
        name="Profilatura ICC per Stampanti DTF"
        description="Servizio professionale di creazione profili ICC personalizzati per stampanti DTF. Garantisce colori accurati e risultati costanti."
        url="/profilatura"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Profilatura ICC', url: '/profilatura' }
        ]}
      />

      <MainHeader />
      <Navigation />

      <main>
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-primary/10 via-background to-secondary/10 py-16 px-4">
          <div className="container mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
                  Profilatura ICC: La Chiave per Stampe Perfette
                </h1>
                <p className="text-lg text-muted-foreground mb-8">
                  La profilatura ICC è il segreto per ottenere colori accurati e risultati professionali in ogni stampa DTF. Scopri perché è fondamentale per il tuo business.
                </p>
                <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground">
                  Richiedi una Consulenza Gratuita
                </Button>
              </div>
              <div className="relative">
                <img 
                  src={profilaturaImg} 
                  alt="Servizio profilatura ICC per stampanti DTF - calibrazione colori professionale" 
                  className="rounded-xl shadow-2xl w-full"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Video Section */}
        <section className="py-16 px-4 bg-[#0a1628]">
          <div className="container mx-auto">
            <h2 className="text-3xl font-bold text-white text-center mb-8">
              Guarda Come Funziona
            </h2>
            <div className="max-w-4xl mx-auto">
              <div className="relative aspect-video rounded-xl overflow-hidden shadow-2xl">
                <iframe
                  src={videoEmbedUrl}
                  className="w-full h-full"
                  allow="autoplay; encrypted-media"
                  allowFullScreen
                  title="Video tutorial profilatura ICC per stampa DTF"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        {/* What is ICC Profiling */}
        <section className="py-16 px-4 bg-background">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-3xl font-bold text-foreground text-center mb-8">
              Cos'è la Profilatura ICC?
            </h2>
            <div className="prose prose-lg max-w-none text-muted-foreground space-y-4">
              <p>
                La profilatura ICC (International Color Consortium) è un processo di calibrazione che garantisce la riproduzione accurata dei colori dal file digitale alla stampa finale.
              </p>
              <p>
                Ogni stampante, inchiostro e supporto di stampa ha caratteristiche uniche che influenzano il risultato finale. Un profilo ICC personalizzato compensa queste differenze, assicurando che i colori sullo schermo corrispondano esattamente a quelli stampati.
              </p>
              <p className="text-destructive font-medium">
                Senza una corretta profilatura, rischi di ottenere colori sbiaditi, tonalità errate e risultati inconsistenti che possono danneggiare la tua reputazione professionale.
              </p>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 px-4 bg-muted/30">
          <div className="container mx-auto">
            <h2 className="text-3xl font-bold text-foreground text-center mb-4">
              Perché la Profilatura è Fondamentale?
            </h2>
            <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
              Una corretta profilatura ICC trasforma il tuo lavoro da amatoriale a professionale
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => (
                <div key={index} className="bg-background rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                  <div className="text-primary mb-4">{benefit.icon}</div>
                  <h3 className="text-xl font-bold text-foreground mb-2">{benefit.title}</h3>
                  <p className="text-muted-foreground">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-16 px-4 bg-primary">
          <div className="container mx-auto">
            <h2 className="text-3xl font-bold text-primary-foreground text-center mb-12">
              Come Funziona il Nostro Servizio di Profilatura
            </h2>
            <div className="grid md:grid-cols-5 gap-6">
              {steps.map((step, index) => (
                <div key={index} className="text-center">
                  <div className="text-4xl font-bold text-secondary mb-4">{step.number}</div>
                  <h3 className="text-lg font-bold text-primary-foreground mb-2">{step.title}</h3>
                  <p className="text-primary-foreground/80 text-sm">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Comparison Section */}
        <section className="py-16 px-4 bg-background">
          <div className="container mx-auto">
            <h2 className="text-3xl font-bold text-foreground text-center mb-12">
              Con vs Senza Profilatura
            </h2>
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {/* Without Profiling */}
              <div className="bg-destructive/10 rounded-xl p-8 border border-destructive/20">
                <h3 className="text-xl font-bold text-destructive mb-6 flex items-center gap-2">
                  <X className="w-6 h-6" />
                  Senza Profilatura
                </h3>
                <ul className="space-y-3">
                  {withoutProfiling.map((item, index) => (
                    <li key={index} className="flex items-start gap-3 text-muted-foreground">
                      <X className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* With Profiling */}
              <div className="bg-green-500/10 rounded-xl p-8 border border-green-500/20">
                <h3 className="text-xl font-bold text-green-600 mb-6 flex items-center gap-2">
                  <Check className="w-6 h-6" />
                  Con Profilatura
                </h3>
                <ul className="space-y-3">
                  {withProfiling.map((item, index) => (
                    <li key={index} className="flex items-start gap-3 text-muted-foreground">
                      <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 bg-gradient-to-r from-primary to-primary/80">
          <div className="container mx-auto text-center">
            <h2 className="text-3xl font-bold text-primary-foreground mb-4">
              Pronto per Portare le Tue Stampe al Livello Successivo?
            </h2>
            <p className="text-primary-foreground/90 mb-8 max-w-2xl mx-auto">
              Contattaci oggi per una consulenza gratuita e scopri come la profilatura ICC può trasformare il tuo business
            </p>
            <Button size="lg" variant="secondary" className="text-lg px-8">
              Richiedi una Consulenza Gratuita
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default Profilatura;
